import HtmlContent from '@/components/HtmlContent';
import { TransText } from '@/i18n/trans/text';
import { cn } from '@/utils/cn';
import {
  Button,
  Icons,
  Typography,
  type TypographyVariants,
} from '@packages/webshop-design-system';
import { useEffect, useRef, useState, type FC } from 'react';

interface ExpandableTextProps {
  text: string | undefined;
  variant?: TypographyVariants['variant'];
  maxLines?: 1 | 2 | 3 | 4 | 5 | 6 | 'none';
  className?: string;
  isExpanded?: boolean;
  setIsExpanded?: (value: boolean) => void;
  /** Render the text as sanitized HTML (BM-configured information texts) */
  renderHtml?: boolean;
  /**
   * When set, "Read more" invokes this instead of expanding inline (and no
   * "Show less" is rendered) — used to open the information-texts modal
   * (BR-57160).
   */
  onReadMore?: () => void;
}

const ExpandableText: FC<ExpandableTextProps> = ({
  text,
  variant = 'body2',
  maxLines = 2,
  isExpanded: isControlledExpanded,
  className,
  setIsExpanded: setControlledIsExpanded,
  renderHtml = false,
  onReadMore,
}) => {
  const [isUncontrolledExpanded, setIsUncontrolledExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);

  const isExpanded = isControlledExpanded ?? isUncontrolledExpanded;
  const setIsExpanded = setControlledIsExpanded ?? setIsUncontrolledExpanded;

  useEffect(() => {
    const checkClamping = () => {
      if (!textRef.current) return;
      const { scrollHeight, clientHeight } = textRef.current;
      setIsClamped(scrollHeight - 1 > clientHeight); // - 1 is a fix for Firefox
    };

    checkClamping();

    window.addEventListener('resize', checkClamping);
    return () => {
      window.removeEventListener('resize', checkClamping);
    };
  }, [text, isExpanded]);

  if (!text) {
    return null;
  }

  return (
    <div className={cn('mb-3', className)}>
      <div
        ref={textRef}
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          WebkitLineClamp: isExpanded ? 'none' : maxLines,
        }}
        className="transition-all duration-300"
      >
        {/* as="div" for HTML: HtmlContent may contain <p>, which cannot
            nest inside Typography's default <p> */}
        <Typography variant={variant} as={renderHtml ? 'div' : undefined}>
          {renderHtml ? <HtmlContent html={text} /> : text}
        </Typography>
      </div>
      {isClamped && (
        <Button
          variant="ghost"
          data-testid="read-more-button"
          className="h-auto"
          onPress={() => (onReadMore ? onReadMore() : setIsExpanded(true))}
          size="small"
        >
          <TransText i18nKey="readMore" />
          {!onReadMore && <Icons.chevronDown height={12} width={12} />}
        </Button>
      )}
      {isExpanded && (
        <Button
          variant="ghost"
          data-testid="read-less-button"
          className="h-auto"
          onPress={() => setIsExpanded(false)}
          size="small"
        >
          <TransText i18nKey="showLess" />
          <Icons.chevronUp height={12} width={12} />
        </Button>
      )}
    </div>
  );
};

export default ExpandableText;
